import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, ListGroup } from 'react-bootstrap';
import { useMap } from 'react-leaflet';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

const mapboxToken = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;

const SearchControl = ({ onLocationSelect }) => {
  const map = useMap();
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSuggestions = debounce(async input => {
    if (!input || !mapboxToken) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          input
        )}.json?access_token=${mapboxToken}&autocomplete=true&limit=5`
      );
      const data = await response.json();
      if (data.features) {
        setSuggestions(
          data.features.map(f => ({
            place_id: f.id,
            description: f.place_name,
            center: f.center, // [lng, lat]
            properties: f.properties
          }))
        );
      }
    } catch (error) {
      console.warn('Failed to fetch suggestions', error);
      setSuggestions([]);
    }
  }, 300);

  const handleInputChange = e => {
    const input = e.target.value;
    setSearchInput(input);
    fetchSuggestions(input);
  };

  const handleSuggestionSelect = suggestion => {
    setIsLoading(true);
    try {
      const [lng, lat] = suggestion.center;

      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
        console.error('Invalid lat/lng:', { lat, lng, suggestion });
        alert('Invalid location selected. Please try another.');
        return;
      }

      const address = suggestion.description || '';
      const accuracy = 20;

      map.setView([lat, lng], 16);
      onLocationSelect({ lat: lat, lng: lng, address, accuracy });
    } catch (error) {
      console.error('Unexpected error selecting location:', error);
      alert('An error occurred while selecting the location.');
    } finally {
      setIsLoading(false);
      setSuggestions([]);
      setSearchInput('');
    }
  };

  return (
    <div
      className="leaflet-top leaflet-left"
      style={{
        position: 'absolute',
        top: '1px',
        zIndex: 1000,
        width: '75%',
        maxWidth: '300px'
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="leaflet-control leaflet-bar">
        <div style={{ borderRadius: '4px', width: '100%', minWidth: '100px' }}>
          <Form>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search location..."
                value={searchInput}
                onChange={handleInputChange}
                disabled={isLoading}
                onFocus={e => e.stopPropagation()}
              />
            </InputGroup>
          </Form>
          <ListGroup
            style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '5px' }}
          >
            {suggestions.map(suggestion => (
              <ListGroup.Item
                key={suggestion.place_id}
                action
                onClick={e => {
                  e.stopPropagation();
                  handleSuggestionSelect(suggestion);
                }}
              >
                {suggestion.description}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      </div>
    </div>
  );
};

SearchControl.propTypes = {
  onLocationSelect: PropTypes.func.isRequired
};

export default SearchControl;
